/**
 * Which step the Become a Mystic wizard opens on.
 *
 * Conceiving, by default. The chart cast when the entry fee is paid is the
 * evidence the Mystic is authored from, so the way in is to conceive and then
 * show the seeker a finished draft. Asking for a theme, an interview and a set
 * of source URLs first is the friction this rework removed — and leaving that
 * front door in place undid the rest of it.
 *
 * Theme, interview and sources are still routable. They are Advanced inputs a
 * seeker may choose to add, not a queue they must clear.
 */
export function entryStep (state) {
  const s = state || {}
  // An explicit destination always wins — that is how Advanced is reached.
  if (s.requested) return s.requested
  // Nothing started, or the last run was already claimed: begin again.
  if (!s.provisioningId || s.claimed) return 'conceiving'
  // Paid and mid-flight: pick it up where it is rather than charging twice.
  return s.status === 'completed' ? 'draft' : 'deriving'
}

/**
 * Has the entry fee been taken by the time the wizard is on this step?
 *
 * Only conceiving-and-past. The threshold step shows what is about to happen and
 * costs nothing: mounting a panel must never spend a seeker's carats, and an
 * earlier version that conceived in onMounted did exactly that on any stray
 * navigation.
 */
export function charged (state) {
  const step = (state && state.step) || ''
  return step === 'deriving' || step === 'draft'
}

/**
 * Same as entryStep, but only resumes a run the server still has.
 *
 * The wizard persists its step and provisioning id in localStorage. When that
 * row is gone — deleted, reset, a different environment — resuming aims the
 * derivation poller at a 404, and it spins on "Starting…" indefinitely with
 * nothing to report and no way out. A missing run is not a run: begin again.
 */
export function resumeStep (state) {
  const step = entryStep(state)
  if ((step === 'deriving' || step === 'draft') && !(state && state.exists)) {
    return 'conceiving'
  }
  return step
}

/**
 * The entry fee short of carats, in plain words (e2e round 2, S38): what is
 * still needed, not the whole price. The 402 from become-mystic/start carries
 * { amount_required, balance }.
 */
export function entryFeeShortLine (data) {
  const d = data || {}
  const required = Number(d.amount_required)
  const balance = Number(d.balance)
  if (!Number.isFinite(required) || required <= 0) return 'You need more carats to open your own practice.'
  if (d.balance === undefined || d.balance === null || !Number.isFinite(balance)) {
    return `You need ${required.toLocaleString('en-US')} carats to open your own practice.`
  }
  const need = Math.ceil(required - balance)
  if (need <= 0) return 'You need more carats to open your own practice.'
  return `You need ${need.toLocaleString('en-US')} more carat${need === 1 ? '' : 's'} to open your own practice.`
}
